import { TrendingUp, CreditCard, CircleDollarSign, Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface SnowballSummaryProps {
  totalDebt: number;
  totalPaid: number;
  progressPercentage: number;
  totalMinimumPayment: number;
  activeDebtsCount: number;
  paidOffDebtsCount: number;
  currency?: string;
}

export function SnowballSummary({
  totalDebt,
  totalPaid,
  progressPercentage,
  totalMinimumPayment,
  activeDebtsCount,
  paidOffDebtsCount,
  currency = 'CRC',
}: SnowballSummaryProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Main Progress Card */}
      <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-primary/20">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <h3 className="text-lg font-medium text-muted-foreground">Progreso Total</h3>
            <div className="relative inline-flex items-center justify-center">
              <svg className="w-40 h-40 transform -rotate-90">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  className="text-muted"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${progressPercentage * 4.4} 440`}
                  strokeLinecap="round"
                  className="text-primary transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold">{progressPercentage.toFixed(0)}%</span>
                <span className="text-sm text-muted-foreground">pagado</span>
              </div>
            </div>
            <div className="flex justify-center gap-8 text-sm">
              <div>
                <p className="text-muted-foreground">Pagado</p>
                <p className="font-bold text-green-600">{formatCurrency(totalPaid)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Pendiente</p>
                <p className="font-bold text-foreground">{formatCurrency(totalDebt)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-red-500/10 rounded-lg shrink-0">
                <CreditCard className="h-4 w-4 text-red-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Deuda total</p>
                <p className="text-sm font-bold truncate">{formatCurrency(totalDebt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-500/10 rounded-lg shrink-0">
                <CircleDollarSign className="h-4 w-4 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Pago mínimo</p>
                <p className="text-sm font-bold truncate">{formatCurrency(totalMinimumPayment)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary/10 rounded-lg shrink-0">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Activas</p>
                <p className="text-sm font-bold">{activeDebtsCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-500/10 rounded-lg shrink-0">
                <Trophy className="h-4 w-4 text-green-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Pagadas</p>
                <p className="text-sm font-bold text-green-600">{paidOffDebtsCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
